/**
 * Sistema de logging condicional para produção
 * Logs só aparecem em desenvolvimento
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
}

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor() {
    // Use process.env.NODE_ENV which is replaced by Vite at build time and works in Jest
    this.isDevelopment = process.env.NODE_ENV !== 'production';

    this.config = {
      enabled: this.isDevelopment,
      level: 'debug',
    };
  }

  /**
   * Log de debug - apenas em desenvolvimento
   */
  debug(message: string, ...args: any[]): void {
    if (this.config.enabled && this.shouldLog('debug')) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }

  /**
   * Log de informação - apenas em desenvolvimento
   */
  info(message: string, ...args: any[]): void {
    if (this.config.enabled && this.shouldLog('info')) {
      console.log(`[INFO] ${message}`, ...args);
    }
  }

  /**
   * Log de aviso - sempre exibido
   */
  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  /**
   * Log de erro - sempre exibido
   */
  error(message: string, error?: any, ...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(`[ERROR] ${message}`, error, ...args);
    }
  }

  /**
   * Agrupa logs relacionados
   */
  group(label: string, callback: () => void): void {
    if (this.config.enabled) {
      console.group(label);
      callback();
      console.groupEnd();
    }
  }

  /**
   * Mede tempo de execução
   */
  time(label: string): void {
    if (this.config.enabled) {
      console.time(label);
    }
  }

  /**
   * Finaliza medição de tempo
   */
  timeEnd(label: string): void {
    if (this.config.enabled) {
      console.timeEnd(label);
    }
  }

  /**
   * Verifica se deve logar baseado no nível
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const requestedLevelIndex = levels.indexOf(level);
    return requestedLevelIndex >= currentLevelIndex;
  }

  /**
   * Configura o logger
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Habilita o logger
   */
  enable(): void {
    this.config.enabled = true;
  }

  /**
   * Desabilita o logger
   */
  disable(): void {
    this.config.enabled = false;
  }

  /**
   * Define o nível de log
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }
}

// Exporta instância singleton
export const logger = new Logger();

// Exporta também a classe para casos especiais
export { Logger };
